import type {
  CreateCompanyContactRequest,
  UpdateCompanyContactRequest
} from '@tandemone/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type ReactNode } from 'react'

import {
  createSupplierBusinessContact,
  deleteSupplierBusinessContact,
  updateSupplierBusinessContact
} from './api'
import { BusinessContactsFormContext } from './business-contacts-context'
import { invalidateOnboarding } from './invalidate-onboarding'

export function BusinessContactsFormProvider({
  children,
  onSuccess
}: {
  children: ReactNode
  onSuccess: () => void
}) {
  const queryClient = useQueryClient()
  const invalidate = () =>
    invalidateOnboarding(queryClient, 'supplier-business-contacts')

  const addMutation = useMutation({
    mutationFn: (draft: CreateCompanyContactRequest) =>
      createSupplierBusinessContact(draft),
    onSuccess: invalidate
  })
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      patch
    }: {
      id: string
      patch: UpdateCompanyContactRequest
    }) => updateSupplierBusinessContact(id, patch),
    onSuccess: invalidate
  })
  const removeMutation = useMutation({
    mutationFn: (id: string) => deleteSupplierBusinessContact(id),
    onSuccess: invalidate
  })

  return (
    <BusinessContactsFormContext.Provider
      value={{
        handleContinue: onSuccess,
        addRow: draft => addMutation.mutateAsync(draft),
        updateRow: async (id, patch) => {
          await updateMutation.mutateAsync({ id, patch })
        },
        removeRow: async id => {
          await removeMutation.mutateAsync(id)
        }
      }}
    >
      {children}
    </BusinessContactsFormContext.Provider>
  )
}
